"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { POST, PUT } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing pseudogateway touch related API-I endpoints
 * @class
 */
class PseudogatewayTouch {
    constructor(api) {
        this.api = api;
    }
    /**
     * Install Gateway from Pseudogateway according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#pseudogateways-install-gateway-from-pseudogateway-post}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!number} args.pseudogatewayId - Required. id of pseudogateway.
     * @param {!object} args.node - Required. The object of the gateway to be installed
     * @param {!number} args.node.propertyId - Required. The property id of the gateway
     * @param {!string} args.node.gatewayIdentifier - Required. The gateway identifier
     * @param {!string} args.node.name - Optional. The name of gateway
     * @param {!string} args.node.location -  Required. The location of the gateway
     * @param {?string[]} args.node.imageUrls - Optional. An array of image urls for the gateway
     * @param {!string} args.node.managementType - Required. Management type of the new node: commercial | research | defunct-retrieve | defunct-abandon | test
     * @param {?number} args.node.maintenanceOwnerId - Optional. id of maintenance owner
     * @param {?number} args.node.assetOwnerId - Optional. id of asset owner
     * @param {?string} args.node.desiredFirmware - Optional. The desired firmware version of the gateway. SN only
     * @param {?string} args.antennaType - Optional. The antenna type of the gateway
     * @param {?string} args.powerSource - Optional. The power source of the gateway. SN only
     * @param {?string} args.antennaExtension - Optional. The type of antenna extension used. SN only
     * @param {?object} args.modem - Optional. The modem model. SN only and required if SN.
     * @param {?string} args.modem.modemId - Optional. An array of image urls for the node
     * @param {?string} args.modem.model - Optional. The modem model. SN only and required if SN
     * @param {?string[]} args.modem.modules - Optional. Modem modules. SN only
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async installGatewayFromPseudogateway(args) {
        const { customerId, force, pseudogatewayId, node: { propertyId, gatewayIdentifier, name, location, imageUrls, managementType, maintenanceOwnerId, assetOwnerId, desiredFirmware, }, modem: { modemId, model, modules } = {}, powerSource, antennaExtension, antennaType, } = args;
        const methodName = 'installGatewayFromPseudogateway';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'pseudoGatewayId', pseudogatewayId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'pseudoGatewayId', pseudogatewayId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'assetOwnerId', assetOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'desiredFirmware', desiredFirmware, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'powerSource', powerSource, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'antennaExtension', antennaExtension, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'antennaType', antennaType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'modemId', modemId, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'model', model, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'modules', modules, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'force', force, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/pseudogateways/${pseudogatewayId}/install`;
        const input = {
            header: {
                customerId,
            },
            query: {
                force,
            },
            body: {
                customerId,
                force,
                node: {
                    propertyId,
                    gatewayIdentifier,
                    name,
                    location,
                    imageUrls,
                    managementType,
                    maintenanceOwnerId,
                    assetOwnerId,
                    desiredFirmware,
                },
                modem: {
                    modemId,
                    model,
                    modules,
                },
                powerSource,
                antennaExtension,
                antennaType,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(POST, route, endPointInput);
    }
    /**
     * Reposition a Pseudogateway according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#pseudogateways-reposition-pseudogateway-put}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!number} args.pseudogatewayId - Required. The specific id of the pseudogateway being used to reposition
     * @param {!string} args.location - Required. The new location of provided pseudogateway
     * @return {promise<void>}
     */
    async repositionPseudogateway(args) {
        const { pseudogatewayId, location, customerId } = args;
        const methodName = 'repositionPseudogateway';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'pseudogateway_id', pseudogatewayId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'pseudogateway_id', pseudogatewayId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/pseudogateways/${pseudogatewayId}/reposition`;
        const input = {
            header: {
                customerId,
            },
            body: {
                location,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
}
exports.default = PseudogatewayTouch;
//# sourceMappingURL=pseudogateway-touch.js.map